import React, { type JSX, ReactNode, useRef, useEffect, useState } from 'react';

import { css } from '@emotion/css';
import { CSSInterpolation, CSSObject } from '@emotion/css/create-instance';
import { createPortal } from 'react-dom';

type ContainerProps<P> = P & { open: boolean };

interface SlideInProps {
    open: boolean;
    onClosed?: () => void;
    children?: ReactNode;
}

export function generatePortalSlideInComponent(
    containerId: string,
    styles: CSSObject | CSSInterpolation,
    slideInAnimationTime: number,
) {
    return function <P extends SlideInProps>(Comp: React.ComponentType<P>) {
        function PortalSlideIn(props: ContainerProps<P>): JSX.Element | null {
            // The class mirrored its props into state so the closing frame
            // could keep rendering the last open props. This ref does the same
            // job: it is only ever written while the slide-in is open.
            const lastOpenProps = useRef(props);
            if (props.open) {
                lastOpenProps.current = props;
            }

            const [shouldRender, setShouldRender] = useState(props.open);
            if (props.open && !shouldRender) {
                setShouldRender(true);
            }

            // Read at fire time, as the class read `this.props.onClosed`, so a
            // caller that swaps the callback mid-transition still gets the new
            // one rather than the one captured when the timer was scheduled.
            const onClosedRef = useRef(props.onClosed);
            useEffect(() => {
                onClosedRef.current = props.onClosed;
            }, [props.onClosed]);

            // Replaces the `ongoingCloseTransition` guard: this effect only
            // re-runs when `open` or `shouldRender` change, so an unrelated
            // re-render cannot restart the transition. Its cleanup also clears
            // the timeout on unmount, which the class never did.
            useEffect(() => {
                if (props.open || !shouldRender) {
                    return;
                }
                const timeoutId = setTimeout(() => {
                    setShouldRender(false);
                    onClosedRef.current?.();
                }, slideInAnimationTime);
                return () => clearTimeout(timeoutId);
            }, [props.open, shouldRender]);

            if (!shouldRender) {
                return null;
            }

            const rendered: ContainerProps<P> = props.open
                ? props
                : { ...lastOpenProps.current, open: false };

            return createPortal(
                <SelfOpeningSlideIn {...rendered} styles={styles}>
                    <Comp {...rendered} />
                </SelfOpeningSlideIn>,
                document.getElementById(containerId) as HTMLElement,
            );
        }
        PortalSlideIn.displayName = 'portalSlideIn';
        const component: React.ComponentType<ContainerProps<P>> = PortalSlideIn;
        return component;
    };
}

function SelfOpeningSlideIn(
    props: SlideInProps & { styles: CSSObject | CSSInterpolation },
) {
    const [open, setOpen] = useState(false);

    // Closing has to take effect in the render pass the prop changes in, as the
    // old UNSAFE_componentWillReceiveProps did, so the outgoing transition
    // starts at the same moment as the parent's unmount timer.
    const [previousOpen, setPreviousOpen] = useState(props.open);
    if (previousOpen !== props.open) {
        setPreviousOpen(props.open);
        if (!props.open) {
            setOpen(false);
        }
    }

    // Opening is deferred by a tick, as the old componentDidMount did, so the
    // browser commits the off-screen transform first and the CSS transition
    // actually runs instead of the panel appearing in place.
    useEffect(() => {
        if (!props.open) {
            return;
        }
        const timeoutId = setTimeout(() => setOpen(true), 0);
        return () => clearTimeout(timeoutId);
    }, [props.open]);

    return (
        <div
            className={css(props.styles, {
                transform: !open ? 'translateX(100%)' : 'translateX(0)',
            })}
        >
            {props.children}
        </div>
    );
}
